import { useCallback } from 'react';

import { useConfig } from 'Context/ConfigContext';

const encryptMetaData = (payload) => btoa(JSON.stringify(payload));

const curlyBracketRegExp = /{(\w+)}/g; // Match all brackets with word inside
const spaceRegExp = /\s{2,}/g;
/**
 * Replace string template with user info
 * 1. Replace `{field}` with `userProfile.field` or empty space (if `userProfile.field` is `null` or `undefined`)
 * 2. Replace empty spaces(2 or more) with empty space for remove
 * 3. Trim
 *
 * @example replaceUserInfo('{firstname} {additional5} {lastname}', {firstname: "abc", lastname: "xyz"})
 * 1. `{firstname} {additional5} {lastname}` -> `abc   xyz`
 * 1.1 Replace `{firstname}` with `abcd`
 * 1.1 Replace `{additional5}` with ` `(empty space)
 * 1.1 Replace `{lastname}` with `xyz`
 * 2. `abc   xyz` -> `abc xyz`
 * 2.1 Replace `  `(2 or more empty spaces) with ` `(1 empty space)
 * 3. Trim
 * // return 'abc xyz'
 *
 * @param {string} template string
 * @param {object} userProfile user object
 * @returns {string}
 */
const replaceFileNameTemplate = (template = '', fileNameInfo = {}) =>
  template
    .replace(curlyBracketRegExp, (_, key) => fileNameInfo[key] ?? ' ')
    .replace(spaceRegExp, ' ')
    .trim();

const getUserPassword = (passwordType, userProfile) => {
  const { username = '', additional6 = '' } = userProfile ?? {};
  if (passwordType === 'none') return undefined;
  if (passwordType === 'lastname') {
    return userProfile.lastname?.toLowerCase();
  } else {
    return additional6 || username.substring(username.lastIndexOf('.') + 1);
  }
};

const getPDFFileName = (defaultFileName, watermarkPDFConfig, fileNameInfo) => {
  const { fileNameType, fileNameTemplate } = watermarkPDFConfig ?? {};

  const { firstname = '', lastname = '' } = fileNameInfo ?? {};
  if (fileNameType === 'fullname') {
    return `${firstname} ${lastname}`;
  }
  if (fileNameType === 'template') {
    return replaceFileNameTemplate(fileNameTemplate, fileNameInfo);
  }
  return defaultFileName;
};

const useWatermark = () => {
  const { watermarkApi, watermarkPDF } = useConfig();

  const generatePayload = useCallback(
    ({ filename, userProfile, courseCode: courseCodeData, ...rest } = {}) => {
      const { title, titleTemplate, subtitle, subtitleTemplate, passwordType } = watermarkPDF ?? {};

      const pdfTitle = title ?? replaceFileNameTemplate(titleTemplate, userProfile);
      const pdfSubtitle = subtitle ?? replaceFileNameTemplate(subtitleTemplate, userProfile);
      const userPassword = getUserPassword(passwordType, userProfile);

      const courseCode = courseCodeData.replace('AN', '');
      const fileName = filename.replace(/\.[^/.]+$/, '');
      const fileNameInfo = { ...userProfile, courseCode, fileName };
      const pdfFileName = getPDFFileName(pdfTitle, watermarkPDF, fileNameInfo);

      const payload = {
        userPassword,
        title: encodeURIComponent(pdfTitle),
        fileName: encodeURIComponent(pdfFileName),
        ...rest,
      };

      if (pdfSubtitle) payload.subtitle = encodeURIComponent(pdfSubtitle);

      return payload;
    },
    [watermarkPDF]
  );

  const getMarkedFileUrl = useCallback(
    async (payload, options = { watermarkApi }) => {
      const watermarkApi = options?.watermarkApi;
      if (!watermarkApi) return null;
      return { url: watermarkApi + '?payload=' + encryptMetaData(payload) };
    },
    [watermarkApi]
  );

  return { getMarkedFileUrl, generatePayload };
};

export default useWatermark;
